import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownToLine } from 'lucide-react';
import { motion } from 'framer-motion';
import { WarehouseScopeBar } from '../../components/WarehouseScopeBar';
import {
  warehousesOf, whPurchaseOrdersOf, findCatalogProduct, variantLabelOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(d);
  // eslint-disable-next-line no-unused-vars
  } catch (e) {
    return dateString;
  }
};

// Supplie receipts — warehouse purchase orders that have been received
// (stock that actually arrived from suppliers). Read-only. Org-admin only.
export const OrgWhReceivedTab = ({ org }) => {
  const { version: dataVersion } = useSharedStore();
  const [scope, setScope] = useState('all');
  const accent = org.theme.primary;

  const rows = useMemo(() => {
    const whs = scope === 'all' ? warehousesOf(org.id) : warehousesOf(org.id).filter((w) => w.id === scope);
    const out = [];
    whs.forEach((w) => {
      whPurchaseOrdersOf(w.id)
        .filter((po) => po.status === 'Received')
        .forEach((po) => {
          (po.lines || [po]).forEach((ln, idx) => {
            const product = findCatalogProduct(ln.productId);
            let base = (Number(ln.originalPrice) || 0) * (Number(ln.billedQty) || 0);
            let discAmt = Number(ln.discountAmount) || 0;
            let discPct = Number(ln.discountPercentage) || 0;
            let gross = base;
            if (ln.discountType === 'percentage' && discPct > 0) {
              gross = base - (base * (discPct / 100));
            } else if (discAmt > 0) {
              gross = base - discAmt;
            }
            let amt = ln.amount !== undefined ? Number(ln.amount) : gross;
            out.push({
              id: `${po.id}-${idx}`,
              warehouseName: w.name,
              productName: product?.name || ln.productId || 'Unknown Product',
              variantLabel: variantLabelOf(product, ln.variantId),
              supplier: po.supplier,
              date: po.date,
              qty: (Number(ln.billedQty) || 0) + (Number(ln.bonusQty) || 0),
              amount: amt,
            });
          });
        });
    });
    out.sort((a, b) => ((a.date || '') < (b.date || '') ? 1 : (a.date || '') > (b.date || '') ? -1 : 0));
    return out;
  }, [scope, org.id, dataVersion]);

  // const totalQty = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);

  return (
    <div className="flex flex-col gap-4">
      <WarehouseScopeBar org={org} value={scope} onChange={setScope} accent={accent} />

      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-3xl p-6 shadow-sm flex flex-col gap-4"
      >
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
              <ArrowDownToLine size={16} style={{ color: accent }} aria-hidden="true" />
              Received details
            </h3>
            <p className="text-[11px] font-medium text-slate-500 mt-1">Stock received into your warehouses from suppliers (purchase orders marked Received).</p>
          </div>

        </header>

        {rows.length === 0 ? (
          <div className="py-12 border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No received stock yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                  <th className="py-2 pr-3">Warehouse</th>
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Variant</th>
                  <th className="py-2 pr-3">Supplier</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3 text-right">Qty</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((po) => {
                  let dPart = '—';
                  let tPart = '—';
                  if (po.date) {
                    const dt = formatDateTime(po.date);
                    if (dt.includes(', ')) {
                      [dPart, tPart] = dt.split(', ');
                    } else {
                      dPart = dt;
                    }
                  }
                  return (
                    <tr key={po.id} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                      <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{po.warehouseName}</td>
                      <td className="py-2 pr-3 text-slate-700">{po.productName}</td>
                      <td className="py-2 pr-3 text-slate-600">{po.variantLabel}</td>
                      <td className="py-2 pr-3 text-slate-600">{po.supplier || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600 font-medium">{dPart}</td>
                      <td className="py-2 pr-3 text-slate-500">{tPart}</td>
                      <td className="py-2 pr-3 text-emerald-700 text-right font-bold">{po.qty}</td>
                      <td className="py-2 pr-3 text-slate-700 text-right">₹{(Number(po.amount) || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>
    </div>
  );
};

OrgWhReceivedTab.propTypes = {
  org: PropTypes.object.isRequired,
};
